import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";

export default function PaymentCallback() {
  const [params] = useSearchParams();
  const reference = params.get("reference") || params.get("trxref") || "";
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    if (!reference) {
      setState({ loading: false, data: null, error: "No payment reference found." });
      return;
    }
    api
      .get(`/paystack/verify/${reference}`)
      .then((r) => {
        if (r.data.status === "success") setState({ loading: false, data: r.data, error: "" });
        else setState({ loading: false, data: null, error: "This payment was not completed. You have not been charged twice — if money left your account, contact us with your reference." });
      })
      .catch(() =>
        setState({ loading: false, data: null, error: "We could not confirm this payment just now. If you completed payment, your order is safe — contact us with your reference." })
      );
  }, [reference]);

  const d = state.data;

  return (
    <div className="flex min-h-screen flex-col bg-jawa-ivory" data-testid="payment-callback">
      <Nav />
      <main className="flex flex-1 items-center justify-center px-6 py-32">
        <FadeUp className="w-full max-w-lg border border-jawa-espresso/10 bg-white p-10 text-center">
          {state.loading && (
            <>
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-jawa-turquoise" />
              <h1 className="mt-6 font-serif text-2xl font-medium text-jawa-espresso">Confirming your payment…</h1>
              <p className="mt-2 text-sm text-jawa-espresso/60">This takes a few seconds. Please don't close the page.</p>
            </>
          )}

          {!state.loading && d && (
            <>
              <CheckCircle2 className="mx-auto h-10 w-10 text-jawa-turquoise" />
              <h1 className="mt-6 font-serif text-3xl font-medium text-jawa-espresso" data-testid="payment-success-heading">
                Payment confirmed.
              </h1>
              <p className="mt-3 text-sm text-jawa-espresso/65">
                Reference <span className="font-mono text-xs" data-testid="payment-reference">{d.reference}</span> · ₦
                {d.amount_ngn.toLocaleString()} — a confirmation email is on its way to you.
              </p>
              <p className="mt-4 border-t border-jawa-stone pt-4 text-xs leading-relaxed text-jawa-espresso/60" data-testid="payment-contact-line">
                Questions about your payment or booking? The team is at{" "}
                <a href="mailto:info@jawaafrica.com" className="font-semibold text-jawa-turquoise hover:text-jawa-coral">info@jawaafrica.com</a>{" "}
                or WhatsApp <a href="https://wa.me/2349154379303" target="_blank" rel="noopener noreferrer" className="font-semibold text-jawa-turquoise hover:text-jawa-coral">+234 915 437 9303</a> — quote your reference.
              </p>

              {d.kind === "consultation" && d.booking && (
                <div className="mt-6 border border-jawa-turquoise/40 bg-jawa-ivory p-6 text-left" data-testid="booking-confirmation">
                  <p className="text-xs uppercase tracking-[0.25em] text-jawa-coral">Your consultation</p>
                  <p className="mt-3 font-serif text-xl text-jawa-espresso">
                    {d.booking.date} · {d.booking.start_label} WAT · {d.booking.duration} minutes
                  </p>
                  <p className="mt-2 text-sm text-jawa-espresso/60">
                    Meeting details arrive by email before your appointment. Keep your reference for any changes.
                  </p>
                </div>
              )}

              {d.kind === "membership" && (
                <div className="mt-6 border border-jawa-gold/40 bg-jawa-ivory p-6" data-testid="membership-confirmation">
                  <p className="text-sm text-jawa-espresso/70">
                    Your membership is now active — your members-only A–Z collection is unlocked.
                  </p>
                  <Link
                    to="/members"
                    className="mt-4 inline-block bg-jawa-espresso px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
                  >
                    Go to the member area
                  </Link>
                </div>
              )}

              {d.kind === "product" && (
                <div className="mt-6 border border-jawa-turquoise/40 bg-jawa-ivory p-6" data-testid="product-confirmation">
                  <p className="text-xs uppercase tracking-[0.25em] text-jawa-coral">Your purchase</p>
                  <p className="mt-3 font-serif text-xl text-jawa-espresso" data-testid="product-title">{d.item_title}</p>
                  {d.download_url ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${d.download_url}`}
                      data-testid="product-download-button"
                      className="mt-4 inline-block bg-jawa-espresso px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
                    >
                      Download your guide (PDF)
                    </a>
                  ) : (
                    <p className="mt-2 text-sm text-jawa-espresso/60">Your confirmation email is on its way. Keep your reference — it is your receipt.</p>
                  )}
                </div>
              )}

              {d.lynksim && (
                <div className="mt-6 border border-jawa-espresso/10 p-6 text-left" data-testid="lynksim-reveal">
                  <p className="text-xs uppercase tracking-[0.25em] text-jawa-gold">A thank-you extra</p>
                  <p className="mt-2 text-sm text-jawa-espresso/70">
                    Get <strong>15% off qualifying Lynksim roaming products</strong> with code{" "}
                    <span className="font-mono font-semibold text-jawa-espresso" data-testid="lynksim-code">{d.lynksim.code}</span>{" "}
                    at{" "}
                    <a href={d.lynksim.url} target="_blank" rel="noopener noreferrer" className="text-jawa-turquoise underline">
                      lynksim.com
                    </a>
                    . Qualifying products only; Lynksim is an independent company.
                  </p>
                </div>
              )}

              <Link
                to="/"
                className="mt-8 inline-block text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
              >
                Back to JÀWÀ
              </Link>
            </>
          )}

          {!state.loading && state.error && (
            <>
              <XCircle className="mx-auto h-10 w-10 text-jawa-coral" />
              <h1 className="mt-6 font-serif text-3xl font-medium text-jawa-espresso" data-testid="payment-failed-heading">
                Payment not completed
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-jawa-espresso/65">{state.error}</p>
              {reference && (
                <p className="mt-3 font-mono text-xs text-jawa-espresso/45">Reference: {reference}</p>
              )}
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link
                  to="/consultations"
                  className="bg-jawa-espresso px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral"
                >
                  Try again
                </Link>
                <a
                  href="https://wa.me/2349154379303"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-jawa-espresso/25 px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso transition-colors hover:border-jawa-turquoise"
                >
                  WhatsApp us
                </a>
              </div>
            </>
          )}
        </FadeUp>
      </main>
      <Footer />
    </div>
  );
}
